import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, of, Subject } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { AppRoutes } from 'src/app/app.app-manager-routes';
import { EntityManagerGrant } from 'src/app/models/entity-config';
import { createToastMessageData } from 'src/app/models/message';
import { SecProfilo } from 'src/app/models/security/sec-profilo';
import { SecUtente } from 'src/app/models/security/sec-utente';
import { GestioneRoutes } from 'src/app/modules/gestione/gestione.app-manager-routes';
import { AppInitialized, AppManagerService } from 'src/app/services/app-manager.service';
import { GrantVisibleDirective } from '../../directives/grant-visible.directive';
import { ComparisonOperator, DataSearch, DataSearchValue } from '../../models/data-search';
import { BadgeCountPipe } from '../../pipes/badge-count.pipe';
import { TranslateItemPipe } from '../../pipes/translate-item.pipe';
import { configId, DataService } from '../../services/data.service';
import { DatabaseAlertService } from '../../services/database-alert.service';
import { MenuToggleService } from '../../services/menu-toggle.service';
import {
  MESSAGE_GENERIC,
  MESSAGE_NOTIFICHE_CHANGED,
  MessageHubService,
  NotificationEvent,
  NotificheChangedData
} from '../../services/message-hub.service';
import { NotificheUtenteService } from '../../services/notifiche-utente.service';
import { Resources, TranslateService } from '../../services/translate.service';
import { safeTakeUntilDestroyed } from '../../utils/util';
import { AppBrandComponent } from '../app-brand/app-brand.component';
import { BaseUtenteComponent } from '../base-utente/base-utente.component';

// Il tipo dell'ambiente DB vive ora in DatabaseAlertService, insieme alla logica che lo usa.

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    AppBrandComponent,
    AsyncPipe,
    AutoCompleteModule,
    BadgeCountPipe,
    BadgeModule,
    ButtonModule,
    ChipModule,
    DividerModule,
    DropdownModule,
    GrantVisibleDirective,
    NgClass,
    NgTemplateOutlet,
    OverlayPanelModule,
    RouterLink,
    TooltipModule,
    TranslateItemPipe
  ]
})
export class HeaderComponent extends BaseUtenteComponent {
  appInitialized: AppInitialized;
  notificationNotReadCount$: Observable<number>;
  userProfiles: SecProfilo[];
  currentProfile: SecProfilo;
  notificationNotReadCount: number = 0;
  translateItems: Resources;
  T$!: Observable<Resources>;
  grants: Observable<EntityManagerGrant>;
  possoImpersonare = false;
  stoImpersonando = false;
  usersArray: any[] = [];

  get isDevCurrentProfile(): boolean {
    return this.currentProfile?.cod === 'DEV';
  }

  /** True quando la ricerca impersonamento e' in barra e non nel menu overlay */
  private get hasImpersonaSearch(): boolean {
    return this.possoImpersonare && !this.stoImpersonando;
  }

  /**
   * Layout compatto: controlli utente nel menu overlay. Con la ricerca
   * impersonamento in barra la soglia e' piu' alta, vedi LayoutService.
   */
  get isHeaderCompact(): boolean {
    return this.layoutService.isHeaderCompact(this.hasImpersonaSearch);
  }

  //parte per ricerca utenti
  private searchTermsuserSubject$ = new Subject<string>();

  constructor(
    private readonly router: Router,
    private readonly oidcSecurityService: OidcSecurityService,
    override readonly appManagerService: AppManagerService,
    private readonly translateService: TranslateService,
    private readonly messageHubService: MessageHubService,
    private readonly dataService: DataService,
    private readonly databaseAlert: DatabaseAlertService,
    private readonly notificheUtenteService: NotificheUtenteService,
    readonly menuToggleService: MenuToggleService
  ) {
    super(appManagerService);
  }

  onUtenteInit(): void {
    // Recupera le grants di questa sezione
    this.grants = this.appManagerService.getGrants(AppRoutes.Home.section ?? AppRoutes.Home.id);

    // Request to notification NOT READ
    // Conteggio notifiche non lette (solo COUNT lato server)
    this.notificationNotReadCount$ = this.notificheUtenteService.nonLetteCount();

    this.appManagerService.initialized$
      .pipe(
        switchMap((appInitialized) => {
          if (!appInitialized.initialized) {
            return of(0);
          }

          const defaultProfileId = appInitialized.dtoUtente.utente.currentProfiloId;
          this.appInitialized = appInitialized;
          this.userProfiles = appInitialized.dtoUtente.profiloList;
          this.currentProfile =
            defaultProfileId === 0 ? this.userProfiles?.[0] : this.userProfiles.find((p) => p.id === defaultProfileId);
          this.databaseAlert.setDevProfile(this.userProfiles?.some((p) => p.cod === 'DEV') ?? false);

          if (this.currentProfile) {
            this.possoImpersonare = this.currentProfile.canImpersonate;
            this.oidcSecurityService.userData$.pipe(take(1)).subscribe((u) => {
              if (u.userData.impersonate) {
                this.stoImpersonando = true;
              }
            });
          }

          this.T$ = this.translateService.translatedItems$(['generic.', 'menu.']).pipe(
            tap((translateItems) => {
              this.translateItems = translateItems;
            })
          );
          this.databaseAlert.refresh();

          // request to notification NOT READ
          return this.notificationNotReadCount$;
        }),
        safeTakeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => (this.notificationNotReadCount = data));

    // Listener of new notifications
    this.messageHubService.notificationEvent$.pipe(safeTakeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (!event) {
        return;
      }
      this.receiveMessage(event);
    });

    this.searchTermsuserSubject$
      .pipe(
        switchMap((term: string) => {
          const dataSearch = new DataSearch();
          dataSearch.pageFirst = 0;
          dataSearch.pageSize = 20;
          dataSearch.dataSearchValues = [
            new DataSearchValue(['0'], ['id'], ComparisonOperator.NotEquals),
            new DataSearchValue(['DEV'], ['LivelloProfiloCod'], ComparisonOperator.NotEquals),
            new DataSearchValue(['ENCI'], ['LivelloProfiloCod'], ComparisonOperator.NotEquals),
            new DataSearchValue(
              [term.toLowerCase()],
              ['Nome', 'Cognome', 'CodFiscale', 'Email'],
              ComparisonOperator.Contains
            )
          ];
          dataSearch.dataSortValues = [];
          return this.dataService.searchElements<SecUtente>(
            `${this.dataService.configSettings.restCommonUrl}/secutente`,
            dataSearch
          );
        }),
        safeTakeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => {
        for (const user of data.entities) {
          user.autoCompleteDes = this.getImpersonaOptionLabel(user);
        }
        this.usersArray = data.entities;
      });
  }

  getImpersonaOptionLabel(option: any): string {
    if (option) {
      return option.email + (option.des ? ' - ' + option.des : '');
    }
    return null;
  }

  impersona(event: any): void {
    this.stoImpersonando = true;
    const data = {};
    data['acr_values'] = 'Impersonate:' + event.value.guid;
    this.oidcSecurityService.authorize(configId, {
      customParams: data
    });
  }

  endImpersona(): void {
    this.stoImpersonando = false;
    const data = {};
    data['acr_values'] = 'ending_impersonation_user_guid:' + this.utente.guid;
    this.oidcSecurityService.authorize(configId, { customParams: data });
  }

  logout(): void {
    this.oidcSecurityService.logoff().subscribe();
  }

  navigateToHome(): void {
    void this.router.navigate(['']);
  }

  navigateToProfiloUtente(): void {
    void this.router.navigate([AppRoutes.ProfiloUtente.url]);
  }

  notificationsEvent(_event): void {
    void this.router.navigate([AppRoutes.Gestione.url, GestioneRoutes.GenNotificaList.url], {
      queryParams: GestioneRoutes.GenNotificaList.queryParams
    });
  }

  onProfileChange(event): void {
    this.dataService
      .updateElement(`${this.dataService.configSettings.restCommonUrl}/currentprofile`, {}, event.value)
      .subscribe({
        next: (data) => {
          this.appManagerService.showToastMessage(
            createToastMessageData('success', this.translateItems['generic.profilesuccesschange'])
          );
          this.currentProfile = data;
          this.databaseAlert.refresh();
          this.appManagerService.initUser({ profiloId: event.value }, 'profileChange');
          this.dataService.updateElement(`${this.dataService.configSettings.restCalendarioUrl}/clrchu`, null, 'xut');
        }
      });

    this.navigateToHome();
  }

  private receiveMessage(event: NotificationEvent): void {
    switch (event.type) {
      case MESSAGE_NOTIFICHE_CHANGED: {
        const totale = (event.data as NotificheChangedData).totale;
        if (totale > this.notificationNotReadCount) {
          const message = this.translateService.translate(this.translateItems, 'generic.notifica.new');
          this.appManagerService.showToastMessage(createToastMessageData('success', message));
        }
        this.notificationNotReadCount = totale;
        break;
      }
      case MESSAGE_GENERIC:
        if (event.data.allNotificationsRead) {
          this.notificationNotReadCount = 0;
        }
        break;
    }
  }

  searchUsers(event: any): void {
    const query = event.query.trim();
    this.searchTermsuserSubject$.next(query);
  }
}
